/** Indirect requests (marketing, IT, services, facilities, freight, labor). */
import { getSupplier } from '../../data/suppliers';
import type { ProcurementRequest } from './requests-types';
import {
  DAY,
  NADIA,
  OWEN,
  RACHEL,
  RAY,
  ag,
  approvals,
  approvedBy,
  checks,
  knownSupplier,
  person,
  personName,
  received,
  seedRequest,
  supplierShort,
} from './seed-utils';

const DANIEL = person('daniel-reyes');
const ELENA = person('elena-novak');
const KEVIN = person('kevin-oneill');
const SAM = person('sam-whitfield');
const MARCUS = person('marcus-hale');
const TOM = person('tom-gallagher');

function req1031(): ProcurementRequest {
  return seedRequest({
    id: 'REQ-1031',
    title: 'Pillar Pro contractor campaign refresh',
    description:
      'Refresh of the Pillar Pro contractor campaign for branch counters and digital: new main artwork, short edits and point-of-sale assets for the branch network.',
    requesterId: 'lauren-brooks',
    department: 'Marketing, Pillar Pro',
    costCenter: 'CC-4120',
    categoryId: 'cat-marketing',
    categoryLabel: 'Marketing & media',
    type: 'indirect',
    amountUsd: 184_000,
    neededInDays: -7,
    createdH: 21 * DAY,
    stage: 'done',
    stageNote: 'Statement of work issued under MSA-BRTL-2025-004',
    skippedStages: ['sourcing'],
    buyPath: 'Statement of work under an existing master agreement',
    suppliers: [
      knownSupplier(
        'sup-brightline',
        'Existing supplier. The campaign refresh is covered by the master agreement',
        'MSA-BRTL-2025-004',
      ),
    ],
    checks: checks([
      ['pass', 'No duplicate request.'],
      [
        'pass',
        'Campaign work under MSA-BRTL-2025-004. Below $250K, so no bids needed.',
      ],
      ['pass', 'CC-4120 had enough budget for the full amount.'],
      ['na', 'Not required. No system access or personal data in scope.'],
      [
        'pass',
        'The statement of work uses the master agreement template with milestone billing.',
      ],
      ['pass', `${supplierShort('sup-brightline')} risk 13, Low.`],
    ]),
    approvals: approvals([
      { role: 'Budget owner', approver: NADIA, doneH: 20 * DAY },
      { role: 'Marketing VP', approver: RACHEL, doneH: 20 * DAY - 6 },
      { role: 'Procurement', approver: DANIEL, doneH: 19 * DAY },
    ]),
    linked: [
      {
        kind: 'contract',
        contractId: 'MSA-BRTL-2025-004',
        detail: 'Statement of work issued under this agreement',
      },
    ],
    activitySeeds: [
      received(21 * DAY, 'lauren-brooks', 'through web intake'),
      ag(
        21 * DAY - 1,
        'Classified as Marketing & media, Indirect',
        'Matched MSA-BRTL-2025-004.',
      ),
      approvedBy(20 * DAY, NADIA, 'Budget owner'),
      approvedBy(19 * DAY, DANIEL, 'Procurement'),
      ag(
        18 * DAY,
        `Statement of work issued to ${getSupplier('sup-brightline').name} under MSA-BRTL-2025-004`,
      ),
      ag(
        17 * DAY,
        'Request closed. Milestone billing set up in Accounts Payable.',
      ),
    ],
  });
}

function req1040(): ProcurementRequest {
  const clearview = supplierShort('sup-clearview');
  return seedRequest({
    id: 'REQ-1040',
    title: 'Janitorial scope extension to the Phoenix DC',
    description: `Extend the ${clearview} scope from the Dallas DC to the new mezzanine offices and break rooms at the Phoenix DC, with service on every shift.`,
    requesterId: 'marcus-hale',
    department: 'Distribution operations, Phoenix DC',
    costCenter: 'CC-2240',
    categoryId: 'cat-facilities',
    categoryLabel: 'Facilities & office',
    type: 'indirect',
    amountUsd: 142_000,
    amountNote: `Added to the current ${clearview} scope`,
    recurring: true,
    neededInDays: -5,
    createdH: 19 * DAY,
    stage: 'done',
    stageNote: 'Change order signed, service started',
    skippedStages: ['sourcing'],
    buyPath: 'Change order under MSA-CLVW-2024-003',
    suppliers: [
      knownSupplier(
        'sup-clearview',
        'Incumbent at the Dallas DC. The master agreement allows scope additions by change order',
        'MSA-CLVW-2024-003',
      ),
    ],
    checks: checks([
      ['pass', 'No similar open request for Phoenix DC facilities.'],
      [
        'pass',
        'Scope addition under an existing master agreement. Below $250K a year, so no bids needed.',
        [{ kind: 'contract', contractId: 'MSA-CLVW-2024-003' }],
      ],
      ['pass', 'CC-2240 has $410K remaining.'],
      ['na', 'Not required. No system access or personal data in scope.'],
      [
        'pass',
        'Change order uses the master agreement template. No new terms.',
      ],
      ['pass', `${clearview} risk 17, Low.`],
    ]),
    approvals: approvals([
      { role: 'Budget owner', approver: MARCUS, doneH: 18 * DAY },
      { role: 'Procurement', approver: DANIEL, doneH: 17 * DAY },
    ]),
    linked: [
      {
        kind: 'contract',
        contractId: 'MSA-CLVW-2024-003',
        detail: `Change order under the ${clearview} master agreement`,
      },
    ],
    activitySeeds: [
      received(19 * DAY, 'marcus-hale', 'through web intake'),
      ag(
        19 * DAY - 1,
        'Classified as Facilities & office, Indirect',
        'Matched MSA-CLVW-2024-003.',
      ),
      ag(19 * DAY - 2, 'Checks complete before routing', 'All checks passed.'),
      approvedBy(18 * DAY, MARCUS, 'Budget owner'),
      approvedBy(17 * DAY, DANIEL, 'Procurement'),
      ag(16 * DAY, `Drafted the change order for ${clearview} signature`),
      ag(14 * DAY, 'Change order signed. Request closed.'),
    ],
  });
}

function req1041(): ProcurementRequest {
  return seedRequest({
    id: 'REQ-1041',
    title: 'Q4 freight capacity on the Memphis routes',
    description:
      'Secure dedicated Q4 capacity on the Memphis to Dallas DC and Memphis to Phoenix DC routes through a rate addendum, ahead of peak season.',
    requesterId: 'marcus-hale',
    channel: 'email',
    department: 'Supply chain, Dallas HQ',
    costCenter: 'CC-2210',
    categoryId: 'cat-logistics',
    categoryLabel: 'Logistics & freight',
    type: 'indirect',
    amountUsd: 640_000,
    amountNote: 'Q4 committed volume by route',
    neededInDays: 14,
    createdH: 18 * DAY,
    stage: 'contracting',
    stageNote: 'Rate addendum in Legal review',
    skippedStages: ['sourcing'],
    buyPath: 'Rate addendum under MSA-TRAN-2025-006',
    suppliers: [
      knownSupplier(
        'sup-transcon',
        'Incumbent. Fuel surcharge capped at 18% of base freight (§5.2)',
        'MSA-TRAN-2025-006',
      ),
    ],
    checks: checks([
      ['pass', 'No overlapping route or capacity request.'],
      [
        'flag',
        'Above $500K: Finance approval added to the route. No new bids, route rates are set in the master agreement.',
        [
          {
            kind: 'contract',
            contractId: 'MSA-TRAN-2025-006',
            clause: 'MSA-TRAN-2025-006:5.2',
          },
        ],
      ],
      ['pass', 'CC-2210 has $1.9M remaining.'],
      ['na', 'Not required. No system access or personal data in scope.'],
      [
        'flag',
        'Addendum drafted from the master agreement template. Legal review in progress.',
      ],
      [
        'flag',
        `${supplierShort('sup-transcon')} risk 27, Low, but capacity on the Memphis routes is tightening (RSK-302).`,
        [{ kind: 'risk-alert', alertId: 'RSK-302' }],
      ],
    ]),
    approvals: approvals([
      { role: 'Budget owner', approver: MARCUS, doneH: 17 * DAY },
      { role: 'Finance', approver: SAM, doneH: 15 * DAY },
      { role: 'Legal', approver: ELENA },
    ]),
    linked: [
      {
        kind: 'contract',
        contractId: 'MSA-TRAN-2025-006',
        detail: 'Freight master agreement, Net 45',
      },
      {
        kind: 'risk-alert',
        alertId: 'RSK-302',
        title: 'RSK-302',
        detail: 'Capacity squeeze on the Memphis routes',
      },
    ],
    onApproved: 'ordering',
    onApprovedNote: `All approvals complete. Rate addendum sent to ${supplierShort('sup-transcon')} for signature.`,
    activitySeeds: [
      received(18 * DAY, 'marcus-hale', 'by email'),
      ag(
        18 * DAY - 1,
        'Classified as Logistics & freight, Indirect',
        'Matched MSA-TRAN-2025-006.',
      ),
      approvedBy(17 * DAY, MARCUS, 'Budget owner'),
      approvedBy(15 * DAY, SAM, 'Finance', 'Above $500K.'),
      ag(
        14 * DAY,
        'Drafted the rate addendum from the master agreement template',
      ),
      ag(4 * DAY, `Sent to ${personName('elena-novak')} for legal review`),
      ag(
        DAY,
        `Reminded ${personName('elena-novak')}`,
        'Route capacity is needed before Q4 volume ramps.',
      ),
    ],
  });
}

function req1043(): ProcurementRequest {
  const northstar = supplierShort('sup-northstar');
  return seedRequest({
    id: 'REQ-1043',
    title: 'Document scanners for accounts payable shared services, Dallas HQ',
    description:
      'Replace eight end-of-life document scanners in the accounts payable shared services team with catalog units.',
    requesterId: 'tom-gallagher',
    channel: 'slack',
    department: 'Finance, accounts payable shared services',
    costCenter: 'CC-1130',
    categoryId: 'cat-it-hardware',
    categoryLabel: 'IT hardware',
    type: 'indirect',
    amountUsd: 9_600,
    amountNote: '8 x $1,200 catalog price',
    neededInDays: -6,
    createdH: 15 * DAY,
    stage: 'done',
    stageNote: 'Delivered, PO-4500234 closed',
    skippedStages: ['sourcing', 'contracting'],
    buyPath: 'Catalog order, auto-approved',
    suppliers: [
      knownSupplier(
        'sup-northstar',
        'Catalog peripherals under the pricing agreement',
        'CPA-NSTR-2025-002',
      ),
    ],
    checks: checks([
      ['pass', 'No open or recent document scanner request.'],
      [
        'pass',
        'Catalog item under CPA-NSTR-2025-002, below the $100K auto-approval threshold.',
      ],
      ['pass', 'CC-1130 has $58,000 remaining.'],
      ['pass', 'Model is on the approved hardware list.'],
      ['na', 'Not required. Priced under an existing agreement.'],
      ['pass', `${northstar} risk 14, Low.`],
    ]),
    approvals: approvals([
      {
        role: 'Budget owner',
        approver: TOM,
        doneH: 15 * DAY,
        auto: true,
        note: 'Auto-approved by policy: catalog purchase under $100K',
      },
    ]),
    linked: [
      {
        kind: 'purchase-order',
        poNumber: 'PO-4500234',
        title: 'PO-4500234',
        detail: 'Delivered and closed',
      },
      {
        kind: 'contract',
        contractId: 'CPA-NSTR-2025-002',
        detail: 'Catalog pricing agreement',
      },
    ],
    activitySeeds: [
      received(15 * DAY, 'tom-gallagher', 'through Slack'),
      ag(
        15 * DAY,
        'Classified as IT hardware, Indirect',
        'Catalog price $1,200 under CPA-NSTR-2025-002.',
      ),
      ag(
        15 * DAY,
        `Auto-approved by policy, budget owner ${personName('tom-gallagher')}`,
      ),
      ag(
        15 * DAY,
        `Purchase order PO-4500234 created and sent to ${northstar}`,
      ),
      ag(8 * DAY, 'Goods received at Dallas HQ. Request closed.'),
    ],
  });
}

function req1045(): ProcurementRequest {
  return seedRequest({
    id: 'REQ-1045',
    title: 'Legal matter management subscription',
    description:
      'Replace spreadsheet-based matter tracking in Commercial Legal with a matter management platform. About 25 seats.',
    requesterId: 'elena-novak',
    channel: 'email',
    department: 'Legal, Commercial',
    costCenter: 'CC-1450',
    categoryId: 'cat-it-software',
    categoryLabel: 'IT & software',
    type: 'indirect',
    amountUsd: 64_000,
    amountNote: 'About 25 seats',
    recurring: true,
    neededInDays: 60,
    createdH: 13 * DAY,
    stage: 'sourcing',
    stageNote: 'Quotes in, waiting on security review',
    buyPath: 'Two written quotes, then purchase order',
    suppliers: [],
    checks: checks([
      ['pass', 'No similar open request.'],
      [
        'flag',
        'No agreement in place. Two written quotes are required below $100K. Quote request sent to three vendors.',
      ],
      ['pass', 'CC-1450 has $140,000 remaining.'],
      [
        'flag',
        'The platform will hold privileged documents. Security review is required before award.',
      ],
      ['na', 'The requester is Legal. Contract review stays in-house.'],
      ['na', 'No vendor selected yet.'],
    ]),
    approvals: approvals([
      { role: 'Budget owner', approver: SAM, doneH: 12 * DAY },
      { role: 'IT security', approver: OWEN },
    ]),
    linked: [],
    recommendation: {
      title: 'Compare the quotes, then clear security before award',
      body: 'The agent screened for vendors with a current SOC 2 Type II report and legal-hold support and sent a quote request to three of them. Two quotes are in. Security review is the gate before award.',
    },
    onApproved: 'ordering',
    onApprovedNote:
      'All approvals complete. Purchase order drafted for the selected vendor.',
    activitySeeds: [
      received(13 * DAY, 'elena-novak', 'by email'),
      ag(
        13 * DAY - 1,
        'Classified as IT & software, Indirect',
        'No existing agreement matched.',
      ),
      approvedBy(12 * DAY, SAM, 'Budget owner'),
      ag(
        12 * DAY,
        'Sent quote requests to three vendors',
        'Quotes due in 5 business days.',
      ),
      ag(
        5 * DAY,
        'Two of three quotes received',
        'Third vendor declined to quote.',
      ),
      ag(4 * DAY, `Routed to ${OWEN.name} for security review`),
    ],
  });
}

function req1046(): ProcurementRequest {
  return seedRequest({
    id: 'REQ-1046',
    title: 'Pallet racking inspection and repairs, Dallas DC (18 aisles)',
    description:
      'Annual inspection of the Dallas DC selective pallet racking against the RMI and ANSI MH16.1 rack standard, with repair of damaged uprights and beam connectors found. 18 aisles.',
    requesterId: 'marcus-hale',
    department: 'Distribution operations, Dallas DC',
    costCenter: 'CC-7110',
    categoryId: 'cat-facilities',
    categoryLabel: 'Facilities & equipment services',
    type: 'indirect',
    amountUsd: 87_500,
    amountNote: 'Inspection plus estimated repairs',
    neededInDays: 45,
    createdH: 12 * DAY,
    stage: 'review',
    stageNote: 'Budget exception',
    buyPath: 'Two written quotes, then purchase order',
    suppliers: [
      {
        name: 'Ironclad Rack Services',
        location: 'Fort Worth, TX',
        role: 'Rack inspection and repair. One of two quotes received',
        standing: 'not-onboarded',
      },
    ],
    checks: checks([
      ['pass', 'No similar open request for Dallas DC facilities.'],
      [
        'flag',
        'No agreement with a racking vendor. Two written quotes are required below $100K: one received.',
      ],
      [
        'fail',
        'CC-7110 has $62,000 remaining. The request exceeds it by $25,500, so a budget transfer or phasing is needed.',
      ],
      ['na', 'Not required. No system access or personal data in scope.'],
      [
        'pass',
        'Standard purchase terms apply. Certificate of insurance requested.',
      ],
      [
        'pass',
        'Preliminary screen clear. Full onboarding starts if the vendor is selected.',
      ],
    ]),
    approvals: approvals([
      { role: 'Budget owner', approver: RAY },
      { role: 'Procurement', approver: DANIEL },
    ]),
    linked: [],
    recommendation: {
      title: 'Phase the inspection across two quarters',
      body: 'Inspecting and repairing aisles 1 to 9 now ($58,000) and aisles 10 to 18 next quarter ($29,500) fits the remaining CC-7110 budget and avoids a transfer. The second quote is due Friday.',
    },
    onApproved: 'ordering',
    onApprovedNote:
      'All approvals complete. Purchase order drafted for the selected racking vendor.',
    activitySeeds: [
      received(12 * DAY, 'marcus-hale', 'through web intake'),
      ag(
        12 * DAY - 1,
        'Classified as Facilities & equipment services, Indirect',
      ),
      ag(
        12 * DAY - 2,
        'Budget check failed',
        'CC-7110 is $25,500 short. Suggested phasing in two orders.',
      ),
      ag(12 * DAY - 3, `Routed to ${RAY.name} for budget approval`),
      ag(3 * DAY, `Reminded ${RAY.name}`, 'Second quote is due Friday.'),
    ],
  });
}

function req1047(): ProcurementRequest {
  return seedRequest({
    id: 'REQ-1047',
    title: 'Arc-flash and electrical safety training, control panel shop',
    description:
      'NFPA 70E arc-flash and electrical safety training for 24 Dallas control panel shop technicians in two sessions, ahead of the next control panel certification audit (UL 508A).',
    requesterId: 'priya-raman',
    channel: 'teams',
    department: 'Quality, Dallas control panel shop',
    costCenter: 'CC-3340',
    categoryId: 'cat-prof-services',
    categoryLabel: 'Professional services',
    type: 'indirect',
    amountUsd: 38_000,
    amountNote: '24 technicians, two sessions',
    neededInDays: 40,
    createdH: 11 * DAY,
    stage: 'contracting',
    stageNote: 'Supplier onboarding, waiting on documents',
    skippedStages: ['sourcing'],
    buyPath: 'Single quote with justification',
    suppliers: [
      {
        name: 'Northwind Safety Training',
        role: 'Industrial safety trainer, NFPA 70E and OSHA 10 and 30. Waiting on tax form W-9 and bank letter',
        standing: 'in-onboarding',
      },
    ],
    checks: checks([
      ['pass', 'No similar open request.'],
      [
        'pass',
        'Below $50K: a single quote with written justification is allowed.',
      ],
      ['pass', 'CC-3340 has $210K remaining.'],
      [
        'pass',
        'No system access. Trainers are escorted on site, and a confidentiality agreement is part of the onboarding pack.',
      ],
      ['pass', 'Standard services agreement.'],
      [
        'flag',
        'New supplier. Onboarding ONB-0093 is at 30%, waiting on the tax form (W-9) and a bank letter.',
      ],
    ]),
    approvals: approvals([
      { role: 'Budget owner', approver: MARCUS, doneH: 10 * DAY },
      { role: 'Procurement', approver: DANIEL, doneH: 9 * DAY },
    ]),
    linked: [
      {
        kind: 'onboarding',
        caseId: 'ONB-0093',
        title: 'ONB-0093 Northwind Safety Training',
        detail: 'Waiting on supplier, 30% complete',
      },
    ],
    activitySeeds: [
      received(11 * DAY, 'priya-raman', 'through Teams'),
      ag(
        11 * DAY - 1,
        'Classified as Professional services, Indirect',
        'No existing agreement matched.',
      ),
      approvedBy(10 * DAY, MARCUS, 'Budget owner'),
      approvedBy(9 * DAY, DANIEL, 'Procurement'),
      ag(
        9 * DAY,
        'Started onboarding for Northwind Safety Training',
        'ONB-0093 created.',
      ),
      ag(DAY, 'Reminded the supplier for the tax form (W-9) and bank letter'),
    ],
  });
}

function req1048(): ProcurementRequest {
  const brightline = supplierShort('sup-brightline');
  return seedRequest({
    id: 'REQ-1048',
    title: 'Agency for the Pillar Pro private-label launch',
    description:
      'Agency to lead the launch of the Pillar Pro private-label line: brand identity and packaging, point-of-sale assets for the branch network, a contractor campaign and launch media. Budget of $326K, kickoff in January.',
    requesterId: 'lauren-brooks',
    department: 'Marketing, Pillar Pro',
    costCenter: 'CC-4120',
    categoryId: 'cat-marketing',
    categoryLabel: 'Marketing & media',
    type: 'indirect',
    amountUsd: 326_000,
    amountNote: 'Concept, assets and launch media',
    neededInDays: 95,
    neededByNote: 'Kickoff',
    createdH: 9 * DAY,
    stage: 'sourcing',
    stageNote: 'Bid request drafted, three bids required',
    buyPath: 'Competitive bids, three required above $250K',
    watchers: ['sam-whitfield'],
    suppliers: [
      knownSupplier(
        'sup-brightline',
        'Existing supplier. Eligible to bid, but spend above $250K needs three competitive bids even under a master agreement',
        'MSA-BRTL-2025-004',
      ),
      {
        name: 'Mesquite & Vale Creative',
        location: 'Austin, TX',
        role: 'Brand and launch agency with distributor and channel work',
        standing: 'not-onboarded',
      },
      {
        name: 'Kilnworks Studio',
        location: 'Chicago, IL',
        role: 'Packaging and private-label launches for industrial brands',
        standing: 'not-onboarded',
      },
    ],
    checks: checks([
      [
        'pass',
        `No duplicate. Related: REQ-1031, Pillar Pro contractor campaign refresh (${personName('lauren-brooks')}, 3 weeks ago), complete.`,
        [{ kind: 'request', requestId: 'REQ-1031' }],
      ],
      [
        'flag',
        `Above $250K requires three competitive bids, even with a master agreement in place. ${brightline} can bid, but an award this size has to go out for bids.`,
        [{ kind: 'contract', contractId: 'MSA-BRTL-2025-004' }],
      ],
      [
        'pass',
        'CC-4120 has $412K remaining. This request commits $326K and leaves $86K.',
      ],
      ['na', 'Not required. No system access or personal data in scope.'],
      [
        'flag',
        'A new agency needs Pillar master services terms, ownership of creative assets and trademark terms for the Pillar Pro marks. Legal template attached.',
      ],
      [
        'pass',
        `${brightline} risk 13, Low. New agencies are screened when invited to bid.`,
      ],
    ]),
    approvals: approvals([
      { role: 'Budget owner', approver: NADIA, doneH: 8 * DAY },
      { role: 'Marketing VP', approver: RACHEL, doneH: 7 * DAY },
      { role: 'Procurement', approver: DANIEL },
      { role: 'Legal', approver: ELENA },
    ]),
    linked: [
      {
        kind: 'sourcing-event',
        eventId: 'RFP-2026-018',
        title: 'RFP-2026-018',
        detail:
          'Pillar Pro launch agency, drafted by the agent, waiting for sign-off',
      },
      {
        kind: 'contract',
        contractId: 'MSA-BRTL-2025-004',
        detail: `${brightline} master agreement, eligible to bid`,
      },
      {
        kind: 'request',
        requestId: 'REQ-1031',
        title: 'Pillar Pro contractor campaign refresh',
        detail: 'Related, complete',
      },
    ],
    recommendation: {
      title: `Get competing bids from ${brightline} and two new agencies`,
      body: `Spend above $250K needs three bids, even with MSA-BRTL-2025-004 in place. The agent drafted a bid request from the Pillar Pro launch brief and will invite ${brightline} plus two agencies once Procurement signs off.`,
    },
    onApproved: 'sourcing',
    onApprovedNote: `All approvals complete. Bid request sent to ${brightline} and two agencies.`,
    activitySeeds: [
      received(9 * DAY, 'lauren-brooks', 'through web intake'),
      ag(
        9 * DAY - 1,
        'Classified as Marketing & media, Indirect',
        'Matched MSA-BRTL-2025-004.',
      ),
      ag(
        9 * DAY - 2,
        'Duplicate and policy checks complete',
        'Related request REQ-1031, no duplicate. Three bids required above $250K.',
      ),
      ag(9 * DAY - 3, `Routed to ${NADIA.name} for budget approval`),
      approvedBy(8 * DAY, NADIA, 'Budget owner'),
      approvedBy(7 * DAY, RACHEL, 'Marketing VP'),
      ag(
        5 * DAY,
        `Drafted bid request for ${brightline} and two agencies`,
        'Scope, evaluation weights and timeline taken from the Pillar Pro launch brief.',
      ),
      ag(
        DAY,
        'Bid request drafted. Waiting on Procurement sign-off',
        `Sent to ${personName('daniel-reyes')}.`,
      ),
    ],
  });
}

function req1051(): ProcurementRequest {
  const northstar = supplierShort('sup-northstar');
  const kevin = personName('kevin-oneill');
  return seedRequest({
    id: 'REQ-1051',
    title: '40 laptops for new inside-sales hires in Dallas',
    description:
      'Forty 14-inch business laptops for the new inside-sales hires at the Dallas headquarters, with the standard encrypted image.',
    requesterId: 'kevin-oneill',
    requestedFor: 'Inside-sales hires, Q4 cohort',
    department: 'IT, End-user computing',
    costCenter: 'CC-6210',
    categoryId: 'cat-it-hardware',
    categoryLabel: 'IT hardware',
    type: 'indirect',
    amountUsd: 51_560,
    amountNote: '40 x $1,289 catalog price',
    neededInDays: 38,
    createdH: 4 * DAY,
    stage: 'ordering',
    stageNote: `PO-4500231 issued to ${northstar}`,
    skippedStages: ['sourcing', 'contracting'],
    buyPath: 'Catalog order, auto-approved under $100K',
    suppliers: [
      knownSupplier(
        'sup-northstar',
        'Catalog price $1,289 per 14-inch business laptop, fixed for the term',
        'CPA-NSTR-2025-002',
      ),
    ],
    checks: checks([
      [
        'pass',
        'No open or recent laptop request for Dallas inside sales in the last 90 days.',
      ],
      [
        'pass',
        'Catalog item under CPA-NSTR-2025-002. $51,560 is below the $100,000 catalog auto-approval threshold.',
        [{ kind: 'contract', contractId: 'CPA-NSTR-2025-002' }],
      ],
      ['pass', 'CC-6210 has $231,400 remaining. This request leaves $179,840.'],
      [
        'pass',
        'Model is on the approved hardware list and ships with the standard encrypted image.',
      ],
      [
        'na',
        'Not required. Priced under an existing agreement with no new terms.',
      ],
      ['pass', `${northstar} risk 14, Low. No open alerts.`],
    ]),
    approvals: approvals([
      {
        role: 'Budget owner',
        approver: KEVIN,
        doneH: 4 * DAY,
        auto: true,
        note: 'Auto-approved by policy: catalog purchase under $100K',
      },
    ]),
    linked: [
      {
        kind: 'purchase-order',
        poNumber: 'PO-4500231',
        title: 'PO-4500231',
        detail: `Issued to ${northstar}, delivery in 7 business days`,
      },
      {
        kind: 'contract',
        contractId: 'CPA-NSTR-2025-002',
        detail: 'Catalog pricing agreement',
      },
    ],
    recommendation: {
      title: 'Catalog order placed, sourcing skipped',
      body: 'Catalog pricing is fixed under CPA-NSTR-2025-002 and $51,560 is below the $100,000 threshold, so budget owner approval was automatic and the PO was created immediately.',
    },
    activitySeeds: [
      received(4 * DAY, 'kevin-oneill', 'through web intake'),
      ag(
        4 * DAY,
        'Classified as IT hardware, Indirect',
        `Matched ${northstar} catalog price $1,289 under CPA-NSTR-2025-002.`,
      ),
      ag(4 * DAY, 'Duplicate, policy and budget checks passed'),
      ag(4 * DAY, `Auto-approved by policy, budget owner ${kevin}`),
      ag(4 * DAY, `Purchase order PO-4500231 created and sent to ${northstar}`),
      ag(
        DAY,
        `${northstar} confirmed shipment`,
        'Tracking added to the purchase order.',
      ),
    ],
  });
}

function req1052(): ProcurementRequest {
  const stratus = supplierShort('sup-stratus');
  return seedRequest({
    id: 'REQ-1052',
    title: `Move analytics workloads to ${stratus} Cloud`,
    description: `Migrate the demand forecasting and inventory analytics workloads from Pillar’s own servers to ${stratus} Cloud, about $540K a year of consumption.`,
    requesterId: 'kevin-oneill',
    department: 'IT, Infrastructure',
    costCenter: 'CC-6230',
    categoryId: 'cat-it-software',
    categoryLabel: 'IT & software',
    type: 'indirect',
    amountUsd: 540_000,
    amountNote: 'Consumption within the committed spend',
    recurring: true,
    neededInDays: 75,
    createdH: 3 * DAY,
    stage: 'review',
    stageNote: 'Waiting on IT security',
    skippedStages: ['sourcing', 'contracting'],
    buyPath: 'Consumption under EA-STRT-2024-001, no new PO',
    suppliers: [
      knownSupplier(
        'sup-stratus',
        'Enterprise agreement, $12M a year committed, tracking 18% under',
        'EA-STRT-2024-001',
      ),
    ],
    checks: checks([
      ['pass', 'No overlapping migration request.'],
      [
        'pass',
        'Consumption under EA-STRT-2024-001. No new agreement or PO needed.',
        [{ kind: 'contract', contractId: 'EA-STRT-2024-001' }],
      ],
      [
        'pass',
        'Draws down committed spend, which is tracking 18% under the $12M commitment. No incremental cost.',
      ],
      [
        'flag',
        `${stratus} disclosed a security incident (RSK-303). A SOC 2 update letter is requested before new data moves.`,
        [{ kind: 'risk-alert', alertId: 'RSK-303' }],
      ],
      ['na', 'Not required. Terms follow the enterprise agreement.'],
      [
        'flag',
        `${stratus} risk 52, Medium, driven by the cyber incident (RSK-303).`,
        [{ kind: 'risk-alert', alertId: 'RSK-303' }],
      ],
    ]),
    approvals: approvals([
      { role: 'Budget owner', approver: SAM, doneH: DAY },
      { role: 'IT security', approver: OWEN },
    ]),
    linked: [
      {
        kind: 'contract',
        contractId: 'EA-STRT-2024-001',
        detail: 'Enterprise agreement, notice deadline Nov 2',
      },
      {
        kind: 'risk-alert',
        alertId: 'RSK-303',
        title: 'RSK-303',
        detail: `${stratus} security incident`,
      },
    ],
    recommendation: {
      title: 'Move the workloads, but wait for the SOC 2 update letter',
      body: 'The $540K of yearly spend is absorbed by the commitment, which is tracking 18% under. Moving data before the update letter arrives (RSK-303) would add exposure, so security sign-off is the gate.',
    },
    onApproved: 'ordering',
    onApprovedNote:
      'All approvals complete. Migration cleared. Consumption tracked against EA-STRT-2024-001.',
    activitySeeds: [
      received(3 * DAY, 'kevin-oneill', 'through web intake'),
      ag(
        3 * DAY - 1,
        'Classified as IT & software, Indirect',
        'Matched EA-STRT-2024-001.',
      ),
      ag(
        3 * DAY - 2,
        'Checks complete before routing',
        `Security flag: ${stratus} incident, update letter requested.`,
      ),
      approvedBy(DAY, SAM, 'Budget owner'),
      ag(DAY, `Routed to ${OWEN.name} for security review`),
    ],
  });
}

function req1053(): ProcurementRequest {
  const gently = supplierShort('sup-gently');
  const elena = personName('elena-novak');
  return seedRequest({
    id: 'REQ-1053',
    title: `Expand ${gently} with the procurement agents module`,
    description: `Expand the ${gently} platform subscription with the procurement agents module for indirect procurement. Priced as an order form under the existing master agreement.`,
    requesterId: 'daniel-reyes',
    department: 'Indirect procurement',
    costCenter: 'CC-6240',
    categoryId: 'cat-it-software',
    categoryLabel: 'IT & software',
    type: 'indirect',
    amountUsd: 120_000,
    amountNote: 'On top of the $360,000 platform subscription',
    recurring: true,
    neededInDays: 40,
    createdH: 3 * DAY,
    stage: 'contracting',
    stageNote: 'Legal review of the amendment',
    skippedStages: ['sourcing'],
    buyPath: 'Contract amendment to MSA-GEN-2025-001',
    suppliers: [
      knownSupplier(
        'sup-gently',
        'Incumbent platform. Existing agreement, so an amendment instead of a new contract',
        'MSA-GEN-2025-001',
      ),
    ],
    checks: checks([
      ['pass', 'No open request for the procurement agents module.'],
      [
        'pass',
        'Expansion of an existing agreement under $250K a year. Amendment route, no competitive bids.',
        [{ kind: 'contract', contractId: 'MSA-GEN-2025-001' }],
      ],
      [
        'pass',
        'CC-6240 has $305,000 remaining. $120,000 a year leaves $185,000.',
      ],
      [
        'pass',
        'SOC 2 Type II on file (§12.1). Customer data is not used to train models (§12.4), which covers the new module.',
        [
          {
            kind: 'contract',
            contractId: 'MSA-GEN-2025-001',
            clause: 'MSA-GEN-2025-001:12.1',
          },
          {
            kind: 'contract',
            contractId: 'MSA-GEN-2025-001',
            clause: 'MSA-GEN-2025-001:12.4',
          },
        ],
      ],
      [
        'flag',
        'Amendment needed for a new order form. The data protection terms (Exhibit C) and the 5% renewal price cap (§4.4) carry over unchanged.',
        [
          {
            kind: 'contract',
            contractId: 'MSA-GEN-2025-001',
            clause: 'MSA-GEN-2025-001:4.4',
          },
        ],
      ],
      ['pass', `${gently} risk 9, Low. No open alerts.`],
    ]),
    approvals: approvals([
      { role: 'Budget owner', approver: KEVIN, doneH: 60 },
      { role: 'Finance', approver: SAM, doneH: 48 },
      { role: 'Legal', approver: ELENA },
    ]),
    linked: [
      {
        kind: 'contract',
        contractId: 'MSA-GEN-2025-001',
        detail: 'Amendment, not a new contract',
      },
    ],
    recommendation: {
      title: 'Amend MSA-GEN-2025-001 instead of signing a new contract',
      body: 'The agreement runs to Oct 31, 2027. The amendment adds the module as a new order form. The 5% renewal cap (§4.4) and the AI training restriction (§12.4) already apply, so Legal reviews the order form only.',
    },
    onApproved: 'ordering',
    onApprovedNote: `All approvals complete. Amendment sent to ${gently} for signature. Order form attached.`,
    activitySeeds: [
      received(72, 'daniel-reyes', 'through web intake'),
      ag(
        71,
        'Classified as IT & software, Indirect',
        'Matched MSA-GEN-2025-001. Amendment path.',
      ),
      ag(
        70,
        'Security review not repeated',
        'SOC 2 Type II on file (§12.1), data protection terms in place.',
      ),
      approvedBy(60, KEVIN, 'Budget owner'),
      approvedBy(48, SAM, 'Finance'),
      ag(40, 'Drafted the amendment order form for Legal'),
      ag(
        20,
        `${elena} is reviewing the amendment`,
        'Edits to the wording expected within 2 business days.',
      ),
    ],
  });
}

function req1054(): ProcurementRequest {
  const lauren = personName('lauren-brooks');
  return seedRequest({
    id: 'REQ-1054',
    title: 'Pillar Pro trade show booth: design and build',
    description:
      'Design, build and ship an exhibit booth for the Pillar Pro line at the May 2027 electrical contractors expo. Booth size and show dates are still to be confirmed.',
    requesterId: 'lauren-brooks',
    channel: 'email',
    department: 'Marketing, Pillar Pro',
    costCenter: 'CC-4120',
    categoryId: 'cat-marketing',
    categoryLabel: 'Marketing & media',
    type: 'indirect',
    amountUsd: 85_000,
    amountNote: 'Estimate from the last booth, to be confirmed',
    neededInDays: 210,
    createdH: 2 * DAY,
    stage: 'intake',
    stageNote: 'Agent asked 2 clarifying questions',
    buyPath: 'To be set once scope is confirmed',
    suppliers: [],
    checks: checks([
      ['pass', 'No similar open request.'],
      ['pending', 'Waiting on booth size and show dates.'],
      ['pending', 'Waiting on a confirmed amount.'],
      ['na', 'Not required. No system access or personal data in scope.'],
      ['na', 'Not required yet.'],
      ['pending', 'Waiting on a supplier shortlist.'],
    ]),
    approvals: approvals([
      { role: 'Budget owner', approver: NADIA },
      { role: 'Marketing VP', approver: RACHEL },
    ]),
    linked: [],
    activitySeeds: [
      received(
        2 * DAY,
        'lauren-brooks',
        'by email',
        'Subject: Booth for the May expo.',
      ),
      ag(2 * DAY, 'Classified as Marketing & media, Indirect'),
      ag(
        2 * DAY,
        `Asked ${lauren} 2 questions by email`,
        'What booth size and what show dates should the quote assume?',
      ),
      ag(
        6,
        `Reminded ${lauren}`,
        'No reply yet. The request stays in intake until scope is confirmed.',
      ),
    ],
  });
}

function req1055(): ProcurementRequest {
  return seedRequest({
    id: 'REQ-1055',
    title: 'Control panel shop supplies restock, Dallas',
    description:
      'Monthly restock of wire ferrules, wire duct, terminal markers and cable ties for the Dallas control panel shop from the catalog.',
    requesterId: 'priya-raman',
    channel: 'slack',
    department: 'Control panel shop, Dallas',
    costCenter: 'CC-3350',
    categoryId: 'cat-facilities',
    categoryLabel: 'Facilities & shop supplies',
    type: 'indirect',
    amountUsd: 8_420,
    amountNote: 'Catalog items',
    neededInDays: 3,
    createdH: 2 * DAY,
    stage: 'ordering',
    stageNote: 'PO-4500238 issued to Clearpath',
    skippedStages: ['sourcing', 'contracting'],
    buyPath: 'Catalog order, auto-approved',
    suppliers: [
      {
        name: 'Clearpath Shop Supplies',
        role: 'Catalog supplier for shop supplies',
        standing: 'onboarded',
      },
    ],
    checks: checks([
      ['pass', 'No open restock order for the control panel shop.'],
      ['pass', 'Catalog items under $10K. Auto-approved.'],
      ['pass', 'CC-3350 has $410K remaining.'],
      ['na', 'Not required. Supplies only.'],
      ['na', 'Not required. Standard catalog terms.'],
      ['pass', 'Clearpath onboarding ONB-0079 completed.'],
    ]),
    approvals: approvals([
      {
        role: 'Budget owner',
        approver: MARCUS,
        doneH: 2 * DAY,
        auto: true,
        note: 'Auto-approved by policy: catalog purchase under $10K',
      },
    ]),
    linked: [
      {
        kind: 'purchase-order',
        poNumber: 'PO-4500238',
        title: 'PO-4500238',
        detail: 'Issued to Clearpath',
      },
      {
        kind: 'onboarding',
        caseId: 'ONB-0079',
        title: 'ONB-0079 Clearpath Shop Supplies',
        detail: 'Onboarding completed',
      },
    ],
    activitySeeds: [
      received(2 * DAY, 'priya-raman', 'through Slack'),
      ag(
        2 * DAY,
        `Auto-approved by policy, budget owner ${personName('marcus-hale')}`,
      ),
      ag(2 * DAY, 'Purchase order PO-4500238 created and sent to Clearpath'),
      ag(
        DAY,
        'Clearpath confirmed shipment',
        'Delivery to the Dallas control panel shop expected in 3 days.',
      ),
    ],
  });
}

function req1056(): ProcurementRequest {
  const keystone = supplierShort('sup-keystone');
  return seedRequest({
    id: 'REQ-1056',
    title: '4 temporary warehouse associates at Hayward DC for 12 weeks',
    description:
      'Four temporary forklift-certified warehouse associates at the Hayward DC for 12 weeks to clear the peak-season receiving backlog: 4 associates, 40 hours a week at $45 an hour.',
    requesterId: 'marcus-hale',
    channel: 'teams',
    department: 'Distribution operations, Hayward DC',
    costCenter: 'CC-2235',
    categoryId: 'cat-contingent',
    categoryLabel: 'Contingent labor',
    type: 'indirect',
    amountUsd: 86_400,
    amountNote: '1,920 hours at $45',
    neededInDays: 14,
    createdH: 22,
    stage: 'review',
    stageNote: 'Waiting on budget owner',
    skippedStages: ['sourcing'],
    buyPath: 'Staffing order under MSA-KEYS-2024-010',
    suppliers: [
      knownSupplier(
        'sup-keystone',
        'Incumbent staffing supplier. Markup capped at 32%',
        'MSA-KEYS-2024-010',
      ),
    ],
    checks: checks([
      ['pass', 'No overlapping staffing request for the Hayward DC.'],
      [
        'pass',
        'Within MSA-KEYS-2024-010: rates fixed, markup capped at 32%. Below $100K.',
        [{ kind: 'contract', contractId: 'MSA-KEYS-2024-010' }],
      ],
      ['pass', 'CC-2235 has $210K remaining.'],
      ['na', 'Not required. Badge access only, no system access.'],
      [
        'flag',
        'The California wage change (RSK-304) affects Hayward temp rates. Rate sheet to be confirmed.',
        [{ kind: 'risk-alert', alertId: 'RSK-304' }],
      ],
      ['pass', `${keystone} risk 19, Low.`],
    ]),
    approvals: approvals([
      { role: 'Budget owner', approver: MARCUS },
      { role: 'Procurement', approver: DANIEL },
    ]),
    linked: [
      {
        kind: 'contract',
        contractId: 'MSA-KEYS-2024-010',
        detail: 'Staffing master agreement, markup cap 32%',
      },
      {
        kind: 'risk-alert',
        alertId: 'RSK-304',
        title: 'RSK-304',
        detail: 'California wage change at Hayward',
      },
    ],
    onApproved: 'ordering',
    onApprovedNote: `All approvals complete. Staffing order sent to ${keystone} for 4 associates.`,
    activitySeeds: [
      received(22, 'marcus-hale', 'through Teams'),
      ag(
        21,
        'Classified as Contingent labor, Indirect',
        'Matched MSA-KEYS-2024-010.',
      ),
      ag(
        21,
        'Checks complete before routing',
        'Legal flag: California wage change (RSK-304).',
      ),
      ag(20, `Routed to ${personName('marcus-hale')} for budget approval`),
    ],
  });
}

export function buildIndirectRequests(): ProcurementRequest[] {
  return [
    req1031(),
    req1040(),
    req1041(),
    req1043(),
    req1045(),
    req1046(),
    req1047(),
    req1048(),
    req1051(),
    req1052(),
    req1053(),
    req1054(),
    req1055(),
    req1056(),
  ];
}
